import { useCallback, useRef } from 'react';
import axios from 'axios';
import api from '../utils/apiInterceptor';
import { API_URL } from '../constants/data';
import { KeyboardReturnOutlined } from '@mui/icons-material';

const CATEGORY_COUNT = "categoryDataCache";
const ALERT_DATA = "alertDataCache";
const STATE_DATA = "stateDataCache";
const COMPANY_DATA = "companyDataCache";
const CACHE_EXPIRY_HOURS = 24;

const isCacheValid = (cacheTimestamp) => {
    if (!cacheTimestamp) return false;
    const now = Date.now();
    const age = (now - cacheTimestamp) / (1000 * 60 * 60);
    return age < CACHE_EXPIRY_HOURS;
};

export const useDashboardInitialization = (
    setCategories,
    setCategoryAlertData,
    setCompanyData,
    setStateData,
    setTotalCounts,
    setIsInitialLoading,
    setLoadingProgress,
    setLoadingMessage,
    setIsLoading,
    fetchCategoryAlert,
    fetchSpatialData,
    setIsCategoriesLoading,
    setIsAlertsLoading,
    setIsCompaniesLoading,
    setIsStatesLoading
) => {
    const cancelledRef = useRef(false);

    const getAccessToken = () => {
        return localStorage.getItem("access_token") || "";
    };

    // Helper function - NOT memoized, uses current parameters
    const checkSubcategoryCache = (filters) => {
        try {
            const cached = JSON.parse(localStorage.getItem("currentSubcategoryData") || "{}");
            console.log("cached", cached)
            if (cached?.prompt && cached?.timestamp && cached?.data) {
                const age = (Date.now() - cached.timestamp) / (1000 * 60 * 60);
                if (age < 24) {
                    setCategories(cached.data.categories || {});
                    setTotalCounts(cached.count || 0);
                    const query = cached.query || cached.prompt;
                    return query;
                }
            }
        } catch (error) {
            console.warn("Failed to check subcategory cache:", error);
        }
        return null;
    };

    const fetchAllCategories = async (filters) => {
        try {
            if (setIsCategoriesLoading) setIsCategoriesLoading(true);
            const cached = JSON.parse(localStorage.getItem(CATEGORY_COUNT) || "{}");
            const cachedQuery = checkSubcategoryCache(filters);
            if (cached?.timestamp && isCacheValid(cached.timestamp) && !cachedQuery) {
                console.log("cached in dashboard", cached)
                setCategories(cached.data.categories || {});
                setTotalCounts(cached.count || 0);
                if (!cancelledRef.current) setLoadingProgress(25);
                return;
            }

            if (cachedQuery) {
                const subCached = JSON.parse(localStorage.getItem("currentSubcategoryData") || "{}");
                console.log("subCached", subCached)
                setCategories(subCached?.data?.categories || {});
                setTotalCounts(subCached?.data?.totalCount || subCached?.count || 0);
                return;
            }

            const start_date = filters.startDate
                ? new Date(filters.startDate).toISOString().split('T')[0]
                : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
            const end_date = filters.endDate
                ? new Date(filters.endDate).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0];

            const res = await api.post(`${API_URL}/complaint_distribution_by_es`, {
                start_date: start_date,
                end_date: end_date,
                stateName: "All",
                sectorName: "All",
                categoryName: "All",
                attribute: "sectorName",
                skip: 0,
                size: 50,
            })

            const data = res.data.distribution || [];
            if (cancelledRef.current) return;
            let totalComplaints = 0;
            const baseCategories = data.reduce((acc, item) => {
                if (item.key != null) {
                    acc[item.key] = {
                        prompt: item.categoryPrompt || "",
                        count: item.doc_count,
                        complaintNumbers: ["NA"],
                    };
                }
                totalComplaints += item.doc_count;

                return acc;
            }, {});
            setCategories(baseCategories);
            setTotalCounts(totalComplaints);

            localStorage.setItem(
                CATEGORY_COUNT,
                JSON.stringify({
                    timestamp: Date.now(),
                    data: {
                        categories: baseCategories,
                        totalComplaints,
                    }
                })
            )
        } catch (error) {
            console.error("Failed to fetch all categories:", error);
        } finally {
            if (setIsCategoriesLoading) setIsCategoriesLoading(false);
        }
    }

    // const fetchAllCategories = async (filters) => {
    //     try {
    //         const cached = JSON.parse(localStorage.getItem(CATEGORY_COUNT) || "{}");
    //         if (cached?.timestamp && isCacheValid(cached.timestamp)) {
    //             setCategories(cached.data.categories || {});
    //             setTotalCounts(cached.data.totalComplaints || 0);
    //             if (!cancelledRef.current) setLoadingProgress(25);
    //             return;
    //         }

    //         const res = await api.get(`${API_URL}/get_all_categories`);
    //         const data = res?.data || [];

    //         if (cancelledRef.current) return;

    //         const baseCategories = data.reduce((acc, item) => {
    //             acc[item.category] = {
    //                 prompt: item.categoryPrompt || "",
    //                 count: 0,
    //                 complaintNumbers: [],
    //             };
    //             return acc;
    //         }, {});

    //         setCategories(baseCategories);

    //         const categoryNames = Object.keys(baseCategories);
    //         let totalComplaints = 0;
    //         const updatedCats = { ...baseCategories };

    //         const CHUNK_SIZE = 3;
    //         for (let i = 0; i < categoryNames.length; i += CHUNK_SIZE) {
    //             if (cancelledRef.current) break;
    //             const chunk = categoryNames.slice(i, i + CHUNK_SIZE);

    //             const promises = chunk.map(async (categoryName) => {
    //                 try {
    //                     const response = await axios.post(
    //                         `${API_URL}/get_semantic_rca`,
    //                         {
    //                             query: categoryName,
    //                             skip: 0,
    //                             size: 0,
    //                             start_date: filters?.startDate
    //                                 ? new Date(filters.startDate).toISOString().split("T")[0]
    //                                 : "2025-01-01",
    //                             end_date: filters?.endDate
    //                                 ? new Date(filters.endDate).toISOString().split("T")[0]
    //                                 : "2025-03-30",
    //                             value: filters?.value || 1,
    //                             CityName: filters?.CityName || "All",
    //                             stateName: filters?.stateName || "All",
    //                             complaintType: filters?.complaintType || "All",
    //                             complaintMode: filters?.complaintMode || "All",
    //                             companyName: filters?.companyName || "All",
    //                             complaintStatus: filters?.complaintStatus || "All",
    //                             threshold: filters?.threshold || 1.5,
    //                             complaint_numbers: ["NA"],
    //                         },
    //                         {
    //                             headers: {
    //                                 Accept: "application/json",
    //                                 "Content-Type": "application/json",
    //                                 'Authorization': `Bearer ${getAccessToken()}`
    //                             },
    //                             timeout: 30000,
    //                         }
    //                     );

    //                     const count = response.data?.total_counts || 0;
    //                     const complaintNumbers = response.data?.complaintNumbers || [];

    //                     totalComplaints += count;
    //                     updatedCats[categoryName].count = count;
    //                     updatedCats[categoryName].complaintNumbers = complaintNumbers;
    //                 } catch (err) {
    //                     console.warn(`Failed to fetch for ${categoryName}`, err);
    //                     updatedCats[categoryName].count = 0;
    //                     updatedCats[categoryName].complaintNumbers = [];
    //                 }
    //             });

    //             await Promise.all(promises);

    //             if (!cancelledRef.current) {
    //                 setCategories({ ...updatedCats });
    //                 setTotalCounts(totalComplaints);
    //             }

    //             if (i + CHUNK_SIZE < categoryNames.length) {
    //                 await new Promise(r => setTimeout(r, 200));
    //             }
    //         }

    //         localStorage.setItem(
    //             CATEGORY_COUNT,
    //             JSON.stringify({
    //                 timestamp: Date.now(),
    //                 data: {
    //                     categories: updatedCats,
    //                     totalComplaints,
    //                 },
    //             })
    //         );
    //     } catch (err) {
    //         console.error("Error fetching all categories:", err);
    //     }
    // };

    const fetchAllCategoryAlerts = async (filters) => {
        try {
            if (setIsAlertsLoading) setIsAlertsLoading(true);
            const cached = JSON.parse(localStorage.getItem(ALERT_DATA) || "{}");
            const cachedQuery = checkSubcategoryCache(filters);

            if (cached?.timestamp && isCacheValid(cached.timestamp) && !cachedQuery) {
                setCategoryAlertData(cached.data);
                if (!cancelledRef.current) setLoadingProgress(50);
                return;
            }

            if (cachedQuery) {
                const subCached = JSON.parse(localStorage.getItem("currentSubcategoryData") || "{}");
                const categoriesObj = subCached.data?.categories || {};
                const categoryNames = Object.keys(categoriesObj);

                if (categoryNames.length > 0) {
                    const filtersWithQuery = { ...filters, query: cachedQuery };
                    const data = await fetchCategoryAlert(filtersWithQuery, categoryNames);
                    setCategoryAlertData(data);
                    return;
                }
            }

            let date = filters.endDate
                ? new Date(filters.endDate)
                : new Date();

            date.setMonth(date.getMonth() - 4);

            const start_date = date.toISOString().split('T')[0];
            const end_date = filters.endDate
                ? new Date(filters.endDate).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0];

            const response = await api.post(`${API_URL}/get_category_alert`, {
                last_days: 84,
                given_date: start_date,
                value: filters.value || 1,
                CityName: "All",
                stateName: "All",
                complaintType: "All",
                complaintMode: "All",
                companyName: "All",
                complaintStatus: "All",
                threshold: filters?.threshold || 1.3,
                complaint_numbers: filters?.complaintNumbers || ["NA"]
            });

            const data = response.data || [];
            if (cancelledRef.current) return;

            setCategoryAlertData(data);

            if (filters?.query) {
                localStorage.setItem("subAlertCache", JSON.stringify({
                    timestamp: Date.now(),
                    data,
                    prompt: filters?.query,
                }));
            } else {
                localStorage.setItem(ALERT_DATA, JSON.stringify({
                    timestamp: Date.now(),
                    data,
                }));
            }
        } catch (error) {
            console.error("Error fetching all category alerts:", error);
        } finally {
            if (setIsAlertsLoading) setIsAlertsLoading(false);
        }
    };

    const fetchAllStates = async (filters) => {
        try {
            if (setIsStatesLoading) setIsStatesLoading(true);
            const cached = JSON.parse(localStorage.getItem(STATE_DATA) || "{}");
            const hasQuery = checkSubcategoryCache(filters);

            if (cached?.timestamp && isCacheValid(cached.timestamp) && !hasQuery) {
                setStateData(cached.data);
                if (!cancelledRef.current) setLoadingProgress(75);
                return;
            }

            const subCategoryData = JSON.parse(localStorage.getItem("currentSubcategoryData") || "{}");
            const hasSubcategoryQuery = subCategoryData?.prompt;

            let data = [];

            if (hasSubcategoryQuery) {
                data = await fetchSpatialData(filters, setStateData);
            } else {
                const start_date = filters.startDate
                    ? new Date(filters.startDate).toISOString().split('T')[0]
                    : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
                const end_date = filters.endDate
                    ? new Date(filters.endDate).toISOString().split('T')[0]
                    : new Date().toISOString().split('T')[0];

                const response = await api.post(`${API_URL}/complaint_distribution_by_es`, {
                    start_date: start_date,
                    end_date: end_date,
                    stateName: "All",
                    sectorName: "All",
                    categoryName: "All",
                    complaintMode: "All",
                    companyName: "All",
                    complaintStatus: "All",
                    attribute: "stateName",
                    skip: 0,
                    size: 50,
                });

                data = response.data.distribution || [];
            }

            if (cancelledRef.current) return;
            setStateData(data);

            if (hasSubcategoryQuery) {
                localStorage.setItem("subStateDataCache", JSON.stringify({
                    prompt: filters?.query,
                    timestamp: Date.now(),
                    data: data,
                }));
            } else {
                localStorage.setItem(STATE_DATA, JSON.stringify({
                    timestamp: Date.now(),
                    data: data,
                }));
            }
        } catch (error) {
            console.error("Error fetching all states:", error);
        } finally {
            if (setIsStatesLoading) setIsStatesLoading(false);
        }
    };


    const loadInitialCompanyData = async (filters) => {
        try {
            if (setIsCompaniesLoading) setIsCompaniesLoading(true);
            const cachedQuery = checkSubcategoryCache(filters);

            if (!cachedQuery) {
                const cached = JSON.parse(localStorage.getItem(COMPANY_DATA) || "{}");
                if (cached?.timestamp && isCacheValid(cached.timestamp)) {
                    setCompanyData(cached.data);
                    if (!cancelledRef.current) setLoadingProgress(100);
                    return;
                }
            } else {
                const subCached = JSON.parse(localStorage.getItem("subCompanyCache") || "{}");
                if (subCached?.timestamp && isCacheValid(subCached.timestamp) &&
                    (subCached?.query === cachedQuery || subCached?.prompt === cachedQuery)) {
                    setCompanyData(subCached.data);
                    if (!cancelledRef.current) setLoadingProgress(100);
                    return;
                }
            }

            const start_date = filters.startDate
                ? new Date(filters.startDate).toISOString().split('T')[0]
                : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];

            const end_date = filters.endDate
                ? new Date(filters.endDate).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0];

            const response = await api.post(
                `${API_URL}/get_all_company_distribution`,
                {
                    query: filters.query || "",
                    start_date: start_date,
                    end_date: end_date,
                    value: filters.value || 1,
                    companyName: "All",
                    purchaseCity: 'All',
                    complaintMode: 'All',
                    complaintStatus: 'All',
                    stateName: 'All',
                    threshold: 1.3,
                    complainNumbers: filters.complaint_numbers || ["NA"],
                    skip: 0,
                    size: 25,
                }
            );

            const results = response.data || []

            if (!cancelledRef.current) {
                setCompanyData(results);

                localStorage.setItem(COMPANY_DATA, JSON.stringify({
                    timestamp: Date.now(),
                    data: results
                }));

                setLoadingProgress(100);
            }
        } catch (error) {
            console.error("Error loading initial company data:", error);
            if (!cancelledRef.current) {
                setCompanyData([]);
                setLoadingProgress(100);
            }
        } finally {
            if (setIsCompaniesLoading) setIsCompaniesLoading(false);
        }
    };

    const reinitializeAllData = useCallback(async (filters) => {
        try {
            cancelledRef.current = false;
            setIsInitialLoading(true);
            setLoadingProgress(0);
            setLoadingMessage('Loading...');

            // Execute all fetches in parallel
            await Promise.all([
                loadInitialCompanyData(filters),
                fetchAllStates(filters),
                fetchAllCategories(filters),
                fetchAllCategoryAlerts(filters)
            ]);

            if (!cancelledRef.current) {
                setTimeout(() => {
                    setIsInitialLoading(false);
                }, 500);
            }
        } catch (error) {
            console.error('Error initializing dashboard:', error);
            if (!cancelledRef.current) {
                setIsInitialLoading(false);
            }
        }
    }, [
        setIsInitialLoading,
        setLoadingProgress,
        setLoadingMessage,
        setCategories,
        setTotalCounts,
        setCategoryAlertData,
        setCompanyData,
        setStateData,
        setIsLoading,
        fetchCategoryAlert,
        fetchSpatialData,
        setIsCategoriesLoading,
        setIsAlertsLoading,
        setIsCompaniesLoading,
        setIsStatesLoading
    ]);

    const clearAllCache = useCallback(() => {
        localStorage.removeItem(CATEGORY_COUNT);
        localStorage.removeItem(ALERT_DATA);
        localStorage.removeItem(STATE_DATA);
        localStorage.removeItem(COMPANY_DATA);
        localStorage.removeItem("currentSubcategoryData");
        localStorage.removeItem("subAlertCache");
        localStorage.removeItem("subStateDataCache");
        localStorage.removeItem("subCompanyCache");
    }, []);

    const cancelInitialization = useCallback(() => {
        cancelledRef.current = true;
    }, []);

    return {
        reinitializeAllData,
        clearAllCache,
        cancelInitialization,
    };
};